import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";

const links = [
    { label: "Services", href: "/#services" },
    { label: "Add-Ons", href: "/#addons" },
    { label: "About", href: "/about" },
    { label: "Contact", href: "/#contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleNavClick = (e, href) => {
        if (href.startsWith("/#")) {
            e.preventDefault();
            setOpen(false);
            const id = href.replace("/#", "");
            if (location.pathname !== "/") {
                navigate("/");
                setTimeout(() => {
                    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
                }, 80);
            } else {
                document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
            }
        } else {
            setOpen(false);
        }
    };

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/5"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-12 h-20">
                <Link
                    to="/"
                    data-testid="nav-logo-link"
                    className="flex items-center gap-2 group"
                >
                    <span className="relative inline-flex items-center justify-center w-9 h-9 border border-[#9D4CDD]/50 bg-[#9D4CDD]/10">
                        <span className="font-heading font-black text-[#9D4CDD] text-sm tracking-tight">
                            AC
                        </span>
                        <span className="absolute inset-0 border border-[#9D4CDD]/0 group-hover:border-[#9D4CDD] transition-all" />
                    </span>
                    <span className="font-heading font-black uppercase text-white tracking-[0.18em] text-sm md:text-base">
                        AC Detailing
                    </span>
                </Link>

                <nav className="hidden md:flex items-center gap-10">
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={(e) => handleNavClick(e, l.href)}
                            className="font-heading text-[13px] uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                    <a
                        href="/#contact"
                        data-testid="nav-book-now-btn"
                        onClick={(e) => handleNavClick(e, "/#contact")}
                        className="inline-flex items-center bg-[#9D4CDD] text-white px-5 py-3 font-heading text-[12px] font-bold tracking-[0.2em] uppercase hover:bg-[#B366F9] transition-all hover:shadow-[0_0_24px_rgba(157,76,221,0.45)]"
                    >
                        Book Now
                    </a>
                </nav>

                <button
                    data-testid="nav-mobile-toggle"
                    className="md:hidden p-2 text-white"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Toggle navigation"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="md:hidden bg-[#0A0A0A]/95 backdrop-blur-xl border-b border-white/5 px-6 py-6 flex flex-col gap-5"
                >
                    {links.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            onClick={(e) => handleNavClick(e, l.href)}
                            className="font-heading text-sm uppercase tracking-[0.2em] text-white/80 hover:text-[#9D4CDD] transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                    <a
                        href="/#contact"
                        data-testid="nav-mobile-book-now-btn"
                        onClick={(e) => handleNavClick(e, "/#contact")}
                        className="inline-flex items-center justify-center bg-[#9D4CDD] text-white px-5 py-3 font-heading text-xs font-bold tracking-[0.2em] uppercase"
                    >
                        Book Now
                    </a>
                </div>
            )}
        </header>
    );
}
